import ModeToolPointEditor from "@components/mode_tool/pointEditor";
import {
  IconDeviceFloppy,
  IconPlus,
  IconRoute,
  IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  hasRoutePointMedia,
  hasRoutePointName,
  hasRoutePointRadius,
  hasRoutePointTurnCase,
  hasRoutePointType,
} from "@utils/mode_tool/validation";
import { normalizeDrivingRouteGroupKey } from "@utils/mode_tool/modeRoute";

function getPointValidationIssues(point = {}, selectedMode = "") {
  const issues = [];
  if (!hasRoutePointName(point?.name, point)) {
    issues.push("name");
  }
  if (selectedMode !== "walking") {
    if (!hasRoutePointRadius(point?.radiusMeters)) issues.push("radius > 0");
    if (!hasRoutePointTurnCase(point?.turnCase)) issues.push("turn type");
  }
  if (!hasRoutePointMedia(point)) issues.push("1 image or audio");
  if (!hasRoutePointType(point?.pointType, selectedMode)) {
    issues.push("point type");
  }
  return issues;
}

function resolveRouteGroupDisplay(point = {}) {
  const routeGroup = normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    Number(point?.order ?? 0) || 0,
  );
  const routeGroupName = String(
    point?.routeGroupName ?? point?.route_group_name ?? "",
  ).trim();
  if (!routeGroup && !routeGroupName) return "";
  if (routeGroup === "main") return "";
  return routeGroupName;
}

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function ModeToolPointsList({
  selectedMode,
  modePoints,
  selectedPointId,
  onSelectPoint,
  languages,
  activeLanguageIso,
  onCommitPoint,
  onUploadPointMedia,
  onDeletePoint,
  onSaveModeRedux,
  canSaveModePoints,
  modeValidationHint,
  saveModePointsHint,
  walkingRouteKeys,
  selectedWalkingRouteKey,
  walkingRouteMetaByKey,
  isWalkingRouteShapeEditEnabled,
  onSelectWalkingRoute,
  onAddWalkingRoute,
  onChangeWalkingRouteName,
  onToggleWalkingRouteShapeEdit,
  onStopWalkingRouteShapeEdit,
  onDeleteWalkingRoute,
}) {
  const [isSaving, setIsSaving] = useState(false);
  if (!selectedMode) return null;
  const activeWalkingRouteMeta =
    walkingRouteMetaByKey?.[selectedWalkingRouteKey] || {};
  const activeWalkingRouteNameValue = readLocalizedValue(
    activeWalkingRouteMeta?.name?.[activeLanguageIso],
  );

  return (
    <>
      <div className="mode-route-tool-points">
        {/*  <p className="mode-route-tool-subtitle">
        Type - {String(selectedMode || "").toLowerCase()}
      </p> */}
        {selectedMode === "walking" || selectedMode === "driving" ? (
          <div className="mode-route-tool-routes">
            <div className="mode-route-tool-routes-head">
              <p className="mode-route-tool-subtitle">
                {selectedMode === "walking" ? "Routes" : "Manual route"}
              </p>
              <div className="mode-route-tool-routes-actions">
                {/* Creates a local unsaved walking route (new-*) that can be edited before saving. */}
                <button
                  type="button"
                  className={`app-icon-btn app-icon-btn--add ${isWalkingRouteShapeEditEnabled ? "is-active" : ""}`}
                  aria-label={
                    isWalkingRouteShapeEditEnabled
                      ? "Stop drawing manual route"
                      : "Draw manual route"
                  }
                  title={
                    isWalkingRouteShapeEditEnabled
                      ? "Stop drawing manual route"
                      : "Draw manual route"
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleWalkingRouteShapeEdit?.();
                  }}
                >
                  <IconRoute size={14} />
                </button>
                {selectedMode === "walking" ? (
                  <button
                    type="button"
                    className="app-icon-btn app-icon-btn--add"
                    aria-label="Add walking route"
                    title="Add walking route"
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopWalkingRouteShapeEdit?.();
                      onAddWalkingRoute?.();
                    }}
                  >
                    <IconPlus size={14} />
                  </button>
                ) : null}
              </div>
            </div>
            {selectedMode === "walking" ? (
              <div className="mode-route-tool-route-tabs">
              {(walkingRouteKeys || []).map((routeKey, index) => (
                <div
                  key={routeKey}
                  className={`mode-route-tool-route-chip ${selectedWalkingRouteKey === routeKey ? "active" : ""}`}
                >
                  {/* Select route to load/display only that route's points. */}
                  <button
                    type="button"
                    className={`mode-route-tool-route-tab ${selectedWalkingRouteKey === routeKey ? "active" : ""}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopWalkingRouteShapeEdit?.();
                      onSelectWalkingRoute?.(routeKey);
                    }}
                  >
                    {readLocalizedValue(
                      walkingRouteMetaByKey?.[routeKey]?.name?.[
                        activeLanguageIso
                      ],
                    ) || `Route ${index + 1}`}
                  </button>
                  <button
                    type="button"
                    className="mode-route-tool-route-chip-delete"
                    aria-label={`Delete Route ${index + 1}`}
                    title={`Delete Route ${index + 1}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopWalkingRouteShapeEdit?.();
                      // For local routes this removes only client state; persisted routes call backend delete.
                      onDeleteWalkingRoute?.(routeKey);
                    }}
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              ))}
              </div>
            ) : null}
            {selectedMode === "walking" && selectedWalkingRouteKey ? (
              <div className="mode-route-tool-route-name">
                <p className="tour-add-form-label">route name</p>
                <label className="field-label app-form-field mode-route-field">
                  <input
                    className="field-input"
                    value={activeWalkingRouteNameValue}
                    placeholder={`Write route name (${activeLanguageIso})`}
                    onChange={(event) =>
                      onChangeWalkingRouteName?.(
                        selectedWalkingRouteKey,
                        activeLanguageIso,
                        event.target.value,
                      )
                    }
                  />
                </label>
              </div>
            ) : null}
            {isWalkingRouteShapeEditEnabled ? (
              <p className="mode-route-tool-step-hint">
                Click anywhere on the map to draw the manual route. Drag orange
                points to adjust it. Use the red X to delete an adjustment
                point.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="mode-route-tool-point-list">
          {(modePoints || []).map((point, index) => {
            const namePoint =
              point?.name?.[activeLanguageIso]?.value ||
              point?.name.en?.value ||
              Object.values(point?.name || {}).join(", ");
            const routeGroupDisplay =
              selectedMode === "driving" ? resolveRouteGroupDisplay(point) : "";

            return (
              <div
                key={point.id}
                className={`mode-route-tool-point-accordion ${selectedPointId === point.id ? "open" : ""}`}
              >
                {getPointValidationIssues(point, selectedMode).length ? (
                  <p className="mode-route-tool-step-hint">
                    Missing:{" "}
                    {getPointValidationIssues(point, selectedMode).join(", ")}
                  </p>
                ) : null}
                <div
                  className={`mode-route-tool-point-item ${selectedPointId === point.id ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="mode-route-tool-point-toggle"
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopWalkingRouteShapeEdit?.();
                      onSelectPoint?.(
                        selectedPointId === point.id ? "" : point.id,
                      );
                    }}
                  >
                    <small>{point.routeGroupName} - </small>
                    <span className="mode-route-tool-point-title">
                      <span>{namePoint || `Point ${index + 1}`}</span>
                      {routeGroupDisplay ? (
                        <small className="mode-route-tool-point-group">
                          detour: {routeGroupDisplay}
                        </small>
                      ) : null}
                    </span>
                  </button>
                </div>
                {selectedPointId === point.id ? (
                  <div className="mode-route-tool-point-body">
                    <ModeToolPointEditor
                      point={point}
                      allModePoints={modePoints}
                      selectedMode={selectedMode}
                      languages={languages}
                      activeLanguageIso={activeLanguageIso}
                      onCommitPoint={onCommitPoint}
                      onUploadPointMedia={onUploadPointMedia}
                      onDeletePoint={(pointId) => {
                        onStopWalkingRouteShapeEdit?.();
                        onDeletePoint?.(pointId);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mode-route-tool-steps">
        {!canSaveModePoints && modeValidationHint ? (
          <p className="mode-route-tool-step-hint">{modeValidationHint}</p>
        ) : null}
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${isSaving ? "is-loading" : ""}`}
          aria-label="Save mode points"
          title={
            isSaving
              ? "Saving mode points"
              : canSaveModePoints
              ? "Save mode points"
              : saveModePointsHint || "Calculate route first"
          }
          disabled={!canSaveModePoints || isSaving}
          onClick={async (event) => {
            event.stopPropagation();
            if (!canSaveModePoints || isSaving) return;
            if (typeof onSaveModeRedux !== "function") return;
            onStopWalkingRouteShapeEdit?.();
            setIsSaving(true);
            try {
              await onSaveModeRedux();
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {isSaving ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy />
            </>
          )}
        </button>
      </div>
    </>
  );
}

export default ModeToolPointsList;
